// ============================================
// ALMACEN.JS — Almacen v3: Central · Oficina · Resguardos · Instalados (2026-10-21)
// ============================================
// ALMACEN v3 (rediseno de Ricardo, 21-oct): Central muestra SOLO lo que hay (nunca el catalogo ni "Sin inventario");
// "Tecnicos" pasa a "Resguardos" (cada usuario con cuenta es un sub-almacen); "Compras" sale del almacen y es seccion
// propia del menu (Alta de material desde Central manda ahi); el boton "Solicitudes de material" se quita (lo que pide
// cada expediente se ve en el expediente; lo que falta, en Compras; los datos siguen en la BD). Cada sub-almacen con su kardex.
// Principio de Ricardo: el material siempre tiene un dueno. Una salida ya no es solo "a expediente": es un TRASPASO entre
// sub-almacenes y cada quien responde por lo que tiene en el suyo. Las 5 pestanas viejas (Material para servicios, Material
// para instalaciones, Inventario, Herramientas, Equipos instalados) ensenaban lo mismo; se reemplazan por 4 con contenido
// propio. Solicitudes de material y Compras se conservan (no eran repetidas). Las maquetas de kits y herramientas en
// localStorage se retiraron: las herramientas ya son reales (activos en Central, prestamo a tecnico).
import React, { useState, useEffect, useMemo } from 'react';
import { authHeaders } from './utils/storage';
import { API_BASE_CLI } from './components/helpersClientes';
import { useInventario } from './components/almacen/PestanaInventario';
import PestanaCentral from './components/almacen/PestanaCentral';
import PestanaOficina from './components/almacen/PestanaOficina';
import PestanaResguardos from './components/almacen/PestanaResguardos';
import Kardex from './components/almacen/Kardex';
import { useUsuario } from './context/UsuarioContext';

const PP = "'Poppins', sans-serif";
const COLOR_PRINCIPAL = '#5c9adb';
const COLOR_OSCURO = '#2f5f9e';
const COLOR_FONDO = '#f3f4f4';
const COLOR_GRIS = '#4e5357';
const COLOR_GRIS_CLARO = '#8a8f95';
const COLOR_VERDE = '#5cd4b0';
const COLOR_ROJO = '#e05c5c';

function fechaCorta(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return '—'; }
}

// Pestanas viejas guardadas por ahi -> su equivalente.
const ALIAS = { inventario: 'central', servicios: 'central', instalaciones: 'central', herramientas: 'central', equipos: 'instalados', resguardos: 'tecnicos', solicitudes: 'central', compras: 'central' };
const normalizar = (p) => ALIAS[p] || p || 'central';

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
function Almacen({ pestana, esCelular }) {
  const { puede, accesoTotal, permisosFetched } = useUsuario();
  const tiene = (k) => accesoTotal || (permisosFetched && puede(k));
  const veCentral = tiene('almacen.inventario') || tiene('almacen.compras');
  const veOficina = veCentral || tiene('almacen.oficina');
  const veTecnicos = veCentral || tiene('almacen.tecnicos');
  const veInstalados = veCentral || tiene('almacen.equiposinstalados');
  const inv = useInventario();

  // Pestanas filtradas por la matriz (regla de oro de permisos): lo que no se puede ver, no se pinta.
  const tabs = useMemo(() => [
    ...(veCentral ? [{ id: 'central', label: '📦 Central' }] : []),
    ...(veOficina ? [{ id: 'oficina', label: '🏢 Oficina' }] : []),
    ...(veTecnicos ? [{ id: 'tecnicos', label: '👤 Resguardos' }] : []),
    ...(veInstalados ? [{ id: 'instalados', label: '📷 Instalados' }] : []),
  ], [veCentral, veOficina, veTecnicos, veInstalados]);

  const [tab, setTab] = useState(normalizar(pestana));
  // Una salida de material creada desde el expediente se abre sola en Central.
  const [abrirSalidaId] = useState(() => { try { const v = sessionStorage.getItem('beep_abrir_salida'); sessionStorage.removeItem('beep_abrir_salida'); return v ? Number(v) : null; } catch { return null; } });
  const irACompras = () => { try { window.dispatchEvent(new CustomEvent('beep-ir-tab', { detail: { tab: 'compras' } })); } catch { /* nada */ } };
  useEffect(() => { if (pestana) setTab(normalizar(pestana)); }, [pestana]);
  // Si la pestana pedida no esta permitida, se abre la primera visible.
  const tabActiva = tabs.some(t => t.id === tab) ? tab : (tabs[0] ? tabs[0].id : null);

  return (
    <div style={{ fontFamily: PP, display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <div style={{ fontSize: esCelular ? 20 : 24, fontWeight: 800, color: COLOR_GRIS }}>📦 Almacén</div>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, marginTop: 4 }}>Central · Oficina · Resguardos · Instalados: el material siempre tiene un dueño, las series viven desde la entrada y cada traspaso queda en el kardex</div>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: '8px 14px', minHeight: 40, borderRadius: 12,
              background: tabActiva === t.id ? COLOR_PRINCIPAL : '#fff',
              color: tabActiva === t.id ? '#fff' : COLOR_OSCURO,
              border: `1.5px solid ${COLOR_PRINCIPAL}`,
              fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: PP,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {!permisosFetched && !accesoTotal && <div style={{ color: COLOR_GRIS_CLARO, fontSize: 13 }}>Cargando permisos…</div>}
      {permisosFetched && tabs.length === 0 && <div style={{ color: COLOR_GRIS_CLARO, fontSize: 13 }}>Sin acceso al almacén: pide un permiso de Almacén en Usuarios.</div>}

      {tabActiva === 'central' && <PestanaCentral inv={inv} esCelular={esCelular} abrirSalidaId={abrirSalidaId} onIrACompras={irACompras} />}
      {tabActiva === 'oficina' && <PestanaOficina esCelular={esCelular} />}
      {tabActiva === 'tecnicos' && <PestanaResguardos esCelular={esCelular} />}
      {tabActiva === 'instalados' && <PestanaEquiposInstalados esCelular={esCelular} />}
    </div>
  );
}

// ====== PESTAÑA INSTALADOS (real desde 2026-10-19): series instaladas con candado de sucursal ======
function PestanaEquiposInstalados({ esCelular }) {
  const [series, setSeries] = useState([]);
  const [q, setQ] = useState('');
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [verKardex, setVerKardex] = useState(false);
  const [instaladoId, setInstaladoId] = useState(null);
  useEffect(() => { fetch(`${API_BASE_CLI}/api/inventario/subalmacenes`, { headers: { ...authHeaders() } }).then(r => r.json()).then(d => { const i = (d.subalmacenes || []).find(x => x.tipo === 'instalado'); if (i) setInstaladoId(i.id); }).catch(() => {}); }, []);
  const card = { background: '#fff', borderRadius: 12, padding: 14, boxShadow: '0 2px 6px rgba(0,0,0,0.05)' };
  useEffect(() => {
    let vivo = true; setCargando(true);
    const h = setTimeout(() => {
      fetch(`${API_BASE_CLI}/api/inventario/series?estado=instalado&q=${encodeURIComponent(q.trim())}`, { headers: { ...authHeaders() } })
        .then(r => r.json()).then(d => { if (!vivo) return; if (d.ok) { setSeries(d.series || []); setError(''); } else setError(d.error || 'Error'); })
        .catch(e => vivo && setError(e.message || 'Error')).finally(() => vivo && setCargando(false));
    }, 250);
    return () => { vivo = false; clearTimeout(h); };
  }, [q]);
  const fmtDia = (v) => { if (!v) return '—'; try { return new Date(String(v).slice(0, 10) + 'T12:00:00').toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return '—'; } };
  // Agrupado por cliente / sucursal.
  const grupos = useMemo(() => {
    const m = new Map();
    for (const s of series) { const k = `${s.cliente_nombre || 'Sin cliente'} — ${s.sucursal_nombre || 'Sin sucursal'}`; if (!m.has(k)) m.set(k, []); m.get(k).push(s); }
    return [...m.entries()];
  }, [series]);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, lineHeight: 1.5 }}><strong style={{ color: COLOR_GRIS }}>📷 Instalado en sucursal</strong> · {series.length} equipo{series.length === 1 ? '' : 's'}. Cada pieza instalada con su número de serie queda amarrada a su sucursal, expediente y posición, y al lote del que salió (compra y garantía del proveedor). <strong>Una serie no se repite ni se mueve de sucursal.</strong> La serie se captura desde el expediente › Material, al instalar: la pieza pasa del técnico que la cargaba a este sub-almacén.</div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por serie, cliente, sucursal, expediente o modelo…" style={{ padding: '10px 12px', minHeight: 44, borderRadius: 10, border: '1px solid #d8dde3', fontFamily: PP, fontSize: 16, width: '100%', boxSizing: 'border-box' }} />
        <button onClick={() => setVerKardex(v => !v)} style={{ padding: '8px 12px', minHeight: 44, borderRadius: 10, background: '#fff', color: COLOR_OSCURO, border: `1px solid ${COLOR_OSCURO}`, fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: PP, whiteSpace: 'nowrap' }}>{verKardex ? 'Ocultar kardex' : '📒 Kardex'}</button>
      </div>
      {verKardex && <div style={card}>{instaladoId ? <Kardex filtro={{ subalmacen_id: instaladoId }} titulo="📒 Kardex de Instalado en sucursal" /> : <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO }}>Cargando…</div>}</div>}
      {error && <div style={{ color: COLOR_ROJO, fontSize: 13 }}>⚠️ {error}</div>}
      {cargando && series.length === 0 && <div style={{ color: COLOR_GRIS_CLARO, fontSize: 13 }}>Cargando…</div>}
      {!cargando && series.length === 0 && !error && <div style={{ ...card, textAlign: 'center', padding: 30, color: COLOR_GRIS_CLARO, fontSize: 13 }}>Todavía no hay equipos instalados con serie. Se capturan en el expediente › Material al instalar cada pieza.</div>}
      {grupos.map(([k, lista]) => (
        <div key={k} style={card}>
          <div style={{ fontSize: 14, fontWeight: 800, color: COLOR_OSCURO }}>{k} <span style={{ fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 600 }}>· {lista.length} equipo{lista.length === 1 ? '' : 's'}</span></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
            {lista.map(s => (
              <div key={s.id} style={{ background: COLOR_FONDO, padding: 10, borderRadius: 10, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                {s.imagen_url && !esCelular && <img src={s.imagen_url} alt="" onError={(ev) => { ev.currentTarget.style.display = 'none'; }} style={{ width: 40, height: 40, objectFit: 'contain', borderRadius: 6, background: '#fff', flexShrink: 0 }} />}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLOR_GRIS, overflowWrap: 'anywhere' }}>{s.marca ? `${s.marca} · ` : ''}{s.modelo || s.nombre}</div>
                  <div style={{ fontSize: 12, color: COLOR_OSCURO, fontWeight: 700, fontFamily: 'monospace' }}>S/N {s.serie}{s.posicion ? <span style={{ color: COLOR_GRIS_CLARO, fontFamily: PP, fontWeight: 600 }}> · {s.posicion}</span> : null}</div>
                  <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 2 }}>
                    {s.expediente_folio ? `${s.expediente_folio} · ` : ''}instalado {fmtDia(s.instalado_en)}{s.instalado_por_nombre ? ` por ${s.instalado_por_nombre}` : ''}
                    {s.compra_folio ? ` · lote ${s.compra_folio}${s.fecha_compra ? ` (${fmtDia(s.fecha_compra)})` : ''}` : ' · lote sin compra'}
                    {s.garantia_vence ? <> · <strong style={{ color: s.garantia_vigente ? COLOR_VERDE : COLOR_ROJO }}>garantía proveedor hasta {fmtDia(s.garantia_vence)}</strong></> : ' · garantía proveedor por confirmar'}
                  </div>
                </div>
                <span title="Serie amarrada a su sucursal: no se mueve" style={{ fontSize: 14, flexShrink: 0 }}>🔒</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// A8 (2026-10-21): la pestana "Solicitudes de material" se retiro. Lo que pide cada expediente se ve en el expediente
// (pestana Material) y lo que falta, en la seccion Compras > Por comprar. Las solicitudes siguen en la BD y en la API.

const overlay = { position: 'fixed', inset: 0, background: 'rgba(20,30,50,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 };
const modalCard = { background: '#fff', borderRadius: 16, padding: 22, width: '100%', maxHeight: 'calc(var(--beep-vh) * 92)', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', fontFamily: PP };

export default Almacen;
